import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  createBrand,
  deleteBrand,
  keyExists,
  listBrandRecords,
  syncBrandsFromApi,
  updateBrand,
  type BrandRecord,
} from "@/lib/catalog/brandsStore";
import { productCountForBrand } from "@/lib/catalog/productsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { useGridPagination } from "@/hooks/useGridPagination";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { slugify } from "@/lib/slugify";
import { toast } from "@/components/ui/sonner";
import { Award, CalendarIcon, Edit, Eye, Filter, ImageIcon, LayoutGrid, Link2, List, Loader2, Plus, Search, Tag, Trash2, X } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const LOGO_MAX_BYTES = 1_500_000;
const BRANDS_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatBrandDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type BrandSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "products_asc"
  | "products_desc";

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

const Brands = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "brand_can_view");
  const canCreate = can(user, "brand_can_create");
  const canUpdate = can(user, "brand_can_update");
  const canDelete = can(user, "brand_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<BrandSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterSlug, setFilterSlug] = useState("");
  const [onlyWithLogo, setOnlyWithLogo] = useState(false);
  const [onlyWithoutLogo, setOnlyWithoutLogo] = useState(false);
  const [minProducts, setMinProducts] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<BrandRecord | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPopular, setIsPopular] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BrandRecord | null>(null);
  const [viewBrand, setViewBrand] = useState<BrandRecord | null>(null);
  const [infoDialog, setInfoDialog] = useState<{ title: string; description: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(true);

  const brands = useMemo(() => listBrandRecords(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameNeedle = filterName.trim().toLowerCase();
    const slugNeedle = filterSlug.trim().toLowerCase();
    const minP = Number(minProducts);
    const hasMinP = minProducts.trim() !== "" && Number.isFinite(minP);
    return brands.filter((b) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(b.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(b.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const matchesQuery =
        !q ||
        b.name.toLowerCase().includes(q) ||
        b.key.toLowerCase().includes(q) ||
        b.description.toLowerCase().includes(q);
      const matchesName = !nameNeedle || b.name.toLowerCase().includes(nameNeedle);
      const matchesSlug = !slugNeedle || b.key.toLowerCase().includes(slugNeedle);
      const hasLogo = Boolean(b.logoDataUrl);
      const matchesLogo = (!onlyWithLogo || hasLogo) && (!onlyWithoutLogo || !hasLogo);
      const productCount = productCountForBrand(b.id);
      const matchesProducts = !hasMinP || productCount >= minP;
      return matchesQuery && matchesName && matchesSlug && matchesLogo && matchesProducts;
    });
  }, [
    brands,
    query,
    filterCreatedDate,
    quickDate,
    filterName,
    filterSlug,
    onlyWithLogo,
    onlyWithoutLogo,
    minProducts,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "products_desc":
          return productCountForBrand(b.id) - productCountForBrand(a.id);
        case "products_asc":
          return productCountForBrand(a.id) - productCountForBrand(b.id);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterSlug.trim() ? 1 : 0) +
    (onlyWithLogo ? 1 : 0) +
    (onlyWithoutLogo ? 1 : 0) +
    (minProducts.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterSlug("");
    setOnlyWithLogo(false);
    setOnlyWithoutLogo(false);
    setMinProducts("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterSlug}|${onlyWithLogo ? "1" : "0"}|${onlyWithoutLogo ? "1" : "0"}|${minProducts}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: BRANDS_PAGE_SIZE_OPTIONS,
    defaultPageSize: BRANDS_PAGE_SIZE_OPTIONS[0],
  });

  useEffect(() => {
    let mounted = true;
    const loadFromApi = async () => {
      setIsForbidden(false);
      try {
        await syncBrandsFromApi();
        if (mounted) {
          refresh();
        }
      } catch (error) {
        if (mounted && isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error("[Brands] Load failed", error);
        if (mounted) {
          toast.error("Could not fetch latest brands. Showing cached data.");
        }
      } finally {
        if (mounted) {
          setIsSyncing(false);
        }
      }
    };
    void loadFromApi();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (isSyncing) return;
    console.log("[Brands] Listing displayed", {
      page,
      pageSize,
      rangeStart,
      rangeEnd,
      totalCount,
      totalPages,
      items: paginated,
    });
  }, [isSyncing, page, pageSize, rangeStart, rangeEnd, totalCount, totalPages, paginated]);

  const isNumericId = (id: string) => /^\d+$/.test(id);

  const resolveBrandFromServer = async (brand: BrandRecord): Promise<BrandRecord | null> => {
    if (isNumericId(brand.id)) return brand;
    // If this came from old demo/local data, try to match server record by slug/key.
    try {
      await syncBrandsFromApi();
      refresh();
      const latest = listBrandRecords();
      const match = latest.find((b) => b.key === brand.key || slugify(b.key) === slugify(brand.key));
      return match ?? null;
    } catch (error) {
      console.error("[Brands] resolveBrandFromServer failed", error);
      return null;
    }
  };

  const startCreate = () => {
    if (isSyncing) {
      toast.error("Brands are still loading. Please wait a moment.");
      return;
    }
    setEditing(null);
    setName("");
    setDescription("");
    setIsPopular(false);
    setLogoDataUrl(null);
    setLogoFile(null);
    setOpen(true);
  };

  const startEdit = async (b: BrandRecord) => {
    console.log("[Brands] Edit clicked", b);
    if (isSyncing) {
      toast.error("Brands are still loading. Please wait a moment.");
      return;
    }
    const resolved = await resolveBrandFromServer(b);
    console.log("[Brands] Resolved brand for edit", resolved);
    if (!resolved || !isNumericId(resolved.id)) {
      toast.error("Could not resolve server brand id. Please refresh and try again.");
      return;
    }
    setEditing(resolved);
    setName(resolved.name);
    setDescription(resolved.description);
    setIsPopular(resolved.isPopular);
    setLogoDataUrl(resolved.logoDataUrl);
    setLogoFile(null);
    setOpen(true);
  };

  const save = async () => {
    if (isSaving) return;
    if (!name.trim()) {
      toast.error("Brand name is required.");
      return;
    }
    const autoKey = slugify(name);
    if (!autoKey) {
      toast.error("Brand name must include letters or numbers.");
      return;
    }
    if (keyExists(autoKey, editing?.id)) {
      setInfoDialog({
        title: "Key already in use",
        description: "Another brand already uses a key derived from this name. Choose a different name.",
      });
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        const result = await updateBrand(editing.id, {
          name,
          key: autoKey,
          description,
          isPopular,
          logoFile,
          logoDataUrl,
        });
        toast.success(result?.message || "Brand updated successfully.");
      } else {
        const result = await createBrand({ name, key: autoKey, description, isPopular, logoFile });
        toast.success(result.message);
      }
      setOpen(false);
      refresh();
    } catch (error) {
      console.error("[Brands] Save failed", error);
      toast.error("Unable to save brand. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const requestRemove = async (b: BrandRecord) => {
    if (isSyncing) {
      toast.error("Brands are still loading. Please wait a moment.");
      return;
    }
    const resolved = await resolveBrandFromServer(b);
    if (!resolved || !isNumericId(resolved.id)) {
      toast.error("Could not resolve server brand id. Please refresh and try again.");
      return;
    }
    const n = productCountForBrand(resolved.id);
    if (n > 0) {
      setInfoDialog({
        title: "Cannot delete brand",
        description: `${n} product(s) are linked to “${resolved.name}”. Change or remove the brand on those products first.`,
      });
      return;
    }
    setDeleteTarget(resolved);
  };

  const confirmRemove = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await deleteBrand(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(result.message);
    } catch (error) {
      console.error("[Brands] Delete failed", error);
      toast.error("Unable to delete brand. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const onLogoFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setInfoDialog({
        title: "Invalid file",
        description: "Please choose an image file (PNG, JPG, WebP, etc.).",
      });
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setInfoDialog({
        title: "Logo too large",
        description: `Use an image under ${Math.round(LOGO_MAX_BYTES / 1_000_000)} MB.`,
      });
      return;
    }
    try {
      const url = await readFileAsDataUrl(file);
      setLogoDataUrl(url); // preview only
      setLogoFile(file);
    } catch {
      setInfoDialog({
        title: "Upload failed",
        description: "Could not read the logo file. Try another image.",
      });
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Brands access required"
      message="You don’t have permission to view brands. Ask an administrator to grant brand_can_view_any."
    />
  ) : (
    <Layout title="Brands">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Brands</h1>
            <p className="text-sm text-muted-foreground mt-1">Product marques and labels — attach them when adding products</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add brand
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code, group…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter brands</SheetTitle>
                  <SheetDescription>Refine by name, key, logo, products, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Brand</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Toyota"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Key contains</Label>
                      <Input
                        className="glass-input h-10 font-mono text-sm"
                        value={filterSlug}
                        onChange={(e) => setFilterSlug(e.target.value)}
                        placeholder="e.g. toyota"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Minimum products</Label>
                    <Input
                      className="glass-input h-10"
                      type="number"
                      value={minProducts}
                      onChange={(e) => setMinProducts(e.target.value)}
                      placeholder="e.g. 1"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>With logo only</span>
                      <Checkbox
                        checked={onlyWithLogo}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyWithLogo(next);
                          if (next) setOnlyWithoutLogo(false);
                        }}
                      />
                    </label>
                    <label className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2 text-sm text-muted-foreground cursor-pointer">
                      <span>Without logo only</span>
                      <Checkbox
                        checked={onlyWithoutLogo}
                        onCheckedChange={(checked) => {
                          const next = Boolean(checked);
                          setOnlyWithoutLogo(next);
                          if (next) setOnlyWithLogo(false);
                        }}
                      />
                    </label>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="brands-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={BRANDS_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as BrandSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="products_desc">Most products</SelectItem>
                  <SelectItem value="products_asc">Fewest products</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="brands-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={BRANDS_PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as BrandSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="products_desc">Most products</SelectItem>
                    <SelectItem value="products_asc">Fewest products</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit brand" : "New brand"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Name *</Label>
                <Input
                  className="glass-input h-10"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Toyota"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Textarea
                  className="glass-input min-h-[88px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description…"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="brand-popular" checked={isPopular} onCheckedChange={(c) => setIsPopular(Boolean(c))} />
                <Label htmlFor="brand-popular" className="text-sm text-muted-foreground cursor-pointer">
                  Popular make (shown first in vehicle picker and similar lists)
                </Label>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Logo image</Label>
                <div className="flex flex-wrap items-start gap-3">
                  {logoDataUrl ? (
                    <div className="relative shrink-0">
                      <img
                        src={logoDataUrl}
                        alt=""
                        className="h-20 w-20 rounded-lg object-cover border border-border"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="secondary"
                        className="absolute -top-2 -right-2 h-7 w-7 rounded-full shadow-md"
                        onClick={() => {
                          setLogoDataUrl(null);
                          setLogoFile(null);
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-lg border border-dashed border-border flex items-center justify-center bg-muted/30 shrink-0">
                      <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      type="file"
                      accept="image/*"
                      className="glass-input h-10 pt-1.5 cursor-pointer"
                      onChange={(e) => onLogoFile(e.target.files?.[0])}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">PNG, JPG, WebP — max ~{Math.round(LOGO_MAX_BYTES / 1_000_000)} MB. Stored in browser only.</p>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!viewBrand} onOpenChange={(o) => !o && setViewBrand(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Brand details</DialogTitle>
            </DialogHeader>
            {viewBrand ? (
              <div className="space-y-4 text-sm">
                <div className="flex gap-3 items-start">
                  {viewBrand.logoDataUrl ? (
                    <img src={viewBrand.logoDataUrl} alt="" className="h-16 w-16 rounded-lg object-cover border border-border shrink-0" />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center shrink-0 border border-border/50">
                      <Award className="w-8 h-8 text-muted-foreground/60" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground text-base">{viewBrand.name}</p>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">{viewBrand.key}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-1 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Products</dt>
                    <dd className="text-muted-foreground">{productCountForBrand(viewBrand.id)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Popular</dt>
                    <dd className="text-muted-foreground">{viewBrand.isPopular ? "Yes" : "No"}</dd>
                  </div>
                  {viewBrand.description ? (
                    <div>
                      <dt className="text-xs text-muted-foreground">Description</dt>
                      <dd className="text-muted-foreground whitespace-pre-wrap">{viewBrand.description}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-xs text-muted-foreground">Created</dt>
                    <dd className="text-muted-foreground">{formatBrandDate(viewBrand.createdAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Last updated</dt>
                    <dd className="text-muted-foreground">{formatBrandDate(viewBrand.updatedAt)}</dd>
                  </div>
                </dl>
              </div>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewBrand(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {brands.length === 0
              ? canCreate
                ? "No brands yet. Click “Add brand” to create one."
                : "No brands yet."
              : "No brands match your search."}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <div className="rounded-lg border border-border bg-card text-card-foreground overflow-hidden shadow-sm">
                  <div className="h-1 w-full bg-primary" aria-hidden />
                  <div className="p-4 pt-5 space-y-4">
                    <div className="flex gap-3 items-start">
                      {b.logoDataUrl ? (
                        <img
                          src={b.logoDataUrl}
                          alt=""
                          className="h-12 w-12 rounded-lg object-cover border border-border shrink-0"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0 border border-border/50">
                          <Award className="w-6 h-6 text-muted-foreground/60" />
                        </div>
                      )}
                      <h3 className="text-lg font-bold uppercase tracking-tight text-foreground leading-snug break-words flex-1 min-w-0">
                        {b.name}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                      <div className="flex gap-2.5 items-start min-w-0">
                        <Link2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" strokeWidth={2} />
                        <div className="min-w-0">
                          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Key</div>
                          <div className="font-semibold text-sm text-foreground truncate font-mono" title={b.key}>
                            {b.key}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2.5 items-start min-w-0">
                        <Tag className="w-4 h-4 shrink-0 mt-0.5 text-red-600" strokeWidth={2} />
                        <div className="min-w-0">
                          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Products</div>
                          <div className="font-semibold text-sm text-foreground">{productCountForBrand(b.id)}</div>
                        </div>
                      </div>
                    </div>
                    {b.description ? (
                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{b.description}</p>
                    ) : null}
                    <div className="border-t border-border pt-3">
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Updated: {formatCatalogAuditDate(b.updatedAt)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canView ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 min-w-[5.5rem] gap-2 h-9 text-xs font-medium"
                          onClick={() => setViewBrand(b)}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </Button>
                      ) : null}
                      {canUpdate ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 min-w-[5.5rem] gap-2 h-9 text-xs font-medium"
                          onClick={() => void startEdit(b)}
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Edit
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-9 w-9 shrink-0 p-0 border-destructive/60 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => void requestRemove(b)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    {b.logoDataUrl ? (
                      <img src={b.logoDataUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <Award className="w-8 h-8 text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{b.name}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600 font-mono truncate max-w-[140px]">
                        {b.key}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {productCountForBrand(b.id)} products · Updated {formatCatalogAuditDate(b.updatedAt)}
                    </p>
                    {b.description ? <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{b.description}</p> : null}
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewBrand(b)}>
                        <Eye className="w-3 h-3" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => void startEdit(b)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => void requestRemove(b)}
                        aria-label="Delete brand"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this brand?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmRemove()}
        />

        <ConfirmAlertDialog
          open={!!infoDialog}
          onOpenChange={(o) => !o && setInfoDialog(null)}
          title={infoDialog?.title ?? ""}
          description={infoDialog?.description ?? ""}
          mode="alert"
          variant="warning"
          confirmLabel="OK"
          onConfirm={() => setInfoDialog(null)}
        />
      </div>
    </Layout>
  );
};

export default Brands;
